import { toggleHidden } from 'src/utils/dom-toggle';
import { Context, Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';

/**
 */
export default class ProgressiveContentToggleController extends Controller {
  static targets = ['button', 'buttonPressed', 'item'];

  declare private buttonTarget: HTMLButtonElement;
  declare private buttonPressedTarget: HTMLButtonElement;
  declare private itemTargets: HTMLElement[];

  declare private hasButtonPressedTarget: boolean;
  private initialActiveState: boolean = false;
  private hasPushedState: boolean = false;

  public constructor(context: Context) {
    super(context);

    this.onToggle = this.onToggle.bind(this);
  }

  get isActive(): boolean {
    if (this.hasButtonPressedTarget) {
      return this.buttonTarget.hasAttribute('hidden');
    }

    return this.buttonTarget.getAttribute('aria-pressed') === 'true';
  }

  get activeButtonTarget(): HTMLButtonElement {
    if (this.hasButtonPressedTarget && this.isActive) {
      return this.buttonPressedTarget;
    }

    return this.buttonTarget;
  }

  set isActive(next: boolean) {
    const isLikeInitialState = this.initialActiveState === next;
    this.itemTargets.forEach((itemTarget) => {
      const initialItemTargetHidden =
        itemTarget.getAttribute(
          'data-progressive-content-toggle-initial-hidden-value',
        ) === 'true';
      toggleHidden(
        itemTarget,
        isLikeInitialState ? initialItemTargetHidden : !initialItemTargetHidden,
      );
    });

    if (this.hasButtonPressedTarget) {
      toggleHidden(this.buttonTarget, next);
      toggleHidden(this.buttonPressedTarget, !next);
    } else {
      this.buttonTarget.setAttribute('aria-pressed', next ? 'true' : 'false');
    }
  }

  public connect(): void {
    this.itemTargets.forEach((itemTarget) =>
      itemTarget.setAttribute(
        'data-progressive-content-toggle-initial-hidden-value',
        itemTarget.hasAttribute('hidden') ? 'true' : 'false',
      ),
    );
    this.initialActiveState = this.isActive;
    this.hasPushedState = false;

    this.buttonTarget.addEventListener('click', this.onToggle);
    this.buttonTarget.setAttribute('data-turbo', 'false');

    if (this.hasButtonPressedTarget) {
      this.buttonPressedTarget.addEventListener('click', this.onToggle);
      this.buttonPressedTarget.setAttribute('data-turbo', 'false');
    }
  }

  public disconnect(): void {
    this.buttonTarget.removeEventListener('click', this.onToggle);
    this.buttonTarget.removeAttribute('data-turbo');

    if (this.hasButtonPressedTarget) {
      this.buttonPressedTarget.removeEventListener('click', this.onToggle);
      this.buttonPressedTarget.removeAttribute('data-turbo');
    }

    // Restore item states
    this.itemTargets.forEach((itemTarget) =>
      toggleHidden(
        itemTarget,
        itemTarget.getAttribute(
          'data-progressive-content-toggle-initial-hidden-value',
        ) === 'true',
      ),
    );

    // Restore button states
    if (this.hasButtonPressedTarget) {
      toggleHidden(this.buttonPressedTarget, !this.initialActiveState);
      toggleHidden(this.buttonTarget, this.initialActiveState);
    } else {
      this.buttonTarget.setAttribute(
        'aria-pressed',
        this.initialActiveState ? 'true' : 'false',
      );
    }
  }

  public onToggle(event: Event): void {
    event.preventDefault();

    this.isActive = !this.isActive;
    this.activeButtonTarget.focus();

    if (event.currentTarget instanceof HTMLAnchorElement) {
      Turbo.navigator.history.replace(new URL(event.currentTarget.href));
    }
  }
}
